import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useCustomization } from '@/contexts/CustomizationContext';
import { UserRole } from '@/types';
import { cn } from '@/lib/utils';
import { PAINEL_OFICINA_BASE, PAINEL_OFICINA_V2_PATH, sidebarPainelOficinaHrefAllowed } from '@/lib/painelOficina';
import { SYSTEM_VERSION, LAST_UPDATE_DATE } from '@/constants/version';
import { ChangelogModal } from '@/components/ChangelogModal';
import { KanbanSelector } from './KanbanSelector';
import {
  BarChart3,
  FileText,
  Building2,
  Users,
  Settings,
  X,
  Upload,
  Layers,
  UserCheck,
  CheckCircle,
  Shield,
  ClipboardCheck,
  MessageCircle,
  Cog,
  MessageSquare,
  TrendingUp,
  Smartphone,
  FolderDown,
  Bell,
  ShoppingBag,
  Wrench,
  LayoutGrid,
  Columns,
} from 'lucide-react';
import {
  filterNavigationByModule,
  getSidebarModuleFromPathname,
  sidebarModuleShowsDashboard,
  sidebarModuleShowsKanbanSelector,
} from '@/lib/portalSidebarContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
}

const navigation = [
  { name: 'Títulos', href: '/titulos', icon: FileText, roles: [UserRole.ADMIN] },
  { name: 'Clientes/Fornecedores', href: '/clientes-fornecedores', icon: UserCheck, roles: [UserRole.ADMIN] },
  { name: 'Gestor de Cobranças', href: '/gestor-cobrancas', icon: ClipboardCheck, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Chat Interno', href: '/internal-chat', icon: MessageCircle, roles: [UserRole.ADMIN, UserRole.GESTOR, UserRole.OPERADOR] },
  { name: 'Importação', href: '/importacao', icon: Upload, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Validação Kanban', href: '/kanban-validacao', icon: CheckCircle, roles: [UserRole.ADMIN] },
  { name: 'Relatórios', href: '/relatorios', icon: BarChart3, roles: [UserRole.ADMIN] },
  { name: 'Cobranças WhatsApp', href: '/whatsapp-cobrancas-report', icon: MessageSquare, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Desempenho do Operador', href: '/operador-performance-report', icon: TrendingUp, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Empresas', href: '/empresas', icon: Building2, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Usuários', href: '/usuarios', icon: Users, roles: [UserRole.ADMIN] },
  { name: 'Logs de Acesso', href: '/log-acesso', icon: Shield, roles: [UserRole.ADMIN] },
  { name: 'Etapas Kanban', href: '/kanban-etapas', icon: Layers, roles: [UserRole.ADMIN] },
  { name: 'Configuração de Kanbans', href: '/kanban-config', icon: Cog, roles: [UserRole.ADMIN] },
  { name: 'Configurações', href: '/configuracoes', icon: Settings, roles: [UserRole.ADMIN] },
  { name: 'Aplicativo ZehMotoca', href: '/configuracoes/aplicativo-zehmotoca', icon: Smartphone, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Notificações Push', href: '/push-notifications', icon: Bell, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Downloads', href: '/downloads', icon: FolderDown, roles: [UserRole.ADMIN] },
  { name: 'Mercado do Zeh', href: '/mercado-zeh', icon: ShoppingBag, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Diretoria', href: '/diretoria', icon: LayoutGrid, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Painel Oficina', href: PAINEL_OFICINA_BASE, icon: Wrench, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  { name: 'Painel Oficina v2', href: PAINEL_OFICINA_V2_PATH, icon: Columns, roles: [UserRole.ADMIN, UserRole.GESTOR] },
  // Acesso aos módulos de e-mail agora está centralizado em Configurações > E-mails
];

const dashboardItem = { name: 'Dashboard', href: '/dashboard', icon: BarChart3, roles: [UserRole.ADMIN, UserRole.GESTOR, UserRole.OPERADOR] };

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed = false }) => {
  const { user } = useAuthStore();
  const { pathname } = useLocation();
  const portalModuleId = getSidebarModuleFromPathname(pathname);

  // Se o usuário tem allowedModules definido (lista de hrefs), filtrar por ela; senão, filtrar por role
  const filteredByPermissions = (() => {
    if (!user) return [];
    const hasAllowedModules = user.allowedModules && Array.isArray(user.allowedModules) && user.allowedModules.length > 0;
    if (hasAllowedModules) {
      const allowed = user.allowedModules as string[];
      return navigation.filter((item) => sidebarPainelOficinaHrefAllowed(item.href, allowed));
    }
    return navigation.filter((item) => item.roles.includes(user.role));
  })();

  const filteredNavigation = filterNavigationByModule(filteredByPermissions, portalModuleId);

  // Dashboard só aparece se: não tiver allowedModules (acesso por role) ou se '/dashboard' estiver em allowedModules
  const showDashboardBase =
    user &&
    (!(user.allowedModules && Array.isArray(user.allowedModules) && user.allowedModules.length > 0) ||
      (user.allowedModules as string[]).includes('/dashboard'));

  const showDashboard = Boolean(showDashboardBase) && sidebarModuleShowsDashboard(portalModuleId);
  const showKanbanSelector = sidebarModuleShowsKanbanSelector(portalModuleId);

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-foreground/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border shadow-soft-lg" data-sidebar>
            <SidebarContent
              navigation={filteredNavigation}
              onClose={onClose}
              showCloseButton
              showDashboard={showDashboard ?? undefined}
              showKanbanSelector={showKanbanSelector}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
        isCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-border bg-card px-4 shadow-soft-sm" data-sidebar>
          <SidebarContent
            navigation={filteredNavigation}
            isCollapsed={isCollapsed}
            showDashboard={showDashboard ?? undefined}
            showKanbanSelector={showKanbanSelector}
          />
        </div>
      </div>
    </>
  );
};

interface SidebarContentProps {
  navigation: typeof navigation;
  onClose?: () => void;
  showCloseButton?: boolean;
  isCollapsed?: boolean;
  /** Exibir link do Dashboard (quando false, usuário com allowedModules não escolheu Dashboard). */
  showDashboard?: boolean;
  /** Exibir seletor de Kanban (módulo Cobranças ou menu completo). */
  showKanbanSelector?: boolean;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ 
  navigation, 
  onClose, 
  showCloseButton,
  isCollapsed,
  showDashboard = true,
  showKanbanSelector = true,
}) => {
  const { customization } = useCustomization();
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  
  return (
    <>
      <div className="flex h-14 shrink-0 items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 shrink-0 bg-primary rounded-input flex items-center justify-center overflow-hidden shadow-soft-sm">
            {customization.logoUrl ? (
              <img 
                src={customization.logoUrl} 
                alt="Logo" 
                className="w-full h-full object-contain"
              />
            ) : (
              <FileText className="h-4 w-4 text-primary-foreground" />
            )}
          </div>
          {!isCollapsed && <span className="font-heading text-heading-3 font-semibold text-foreground truncate" data-system-name>{customization.systemName}</span>}
        </div>
        {showCloseButton && (
          <button
            onClick={onClose}
            className="p-2 rounded-input text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
            aria-label="Fechar menu"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              <li>
                <Link
                  to="/portal"
                  onClick={() => onClose?.()}
                  className={cn(
                    'group flex gap-x-3 rounded-input border border-dashed border-primary/40 bg-muted/30 px-3 py-2 font-body text-body-sm font-medium text-foreground transition-all duration-200',
                    'hover:border-primary/60 hover:bg-muted'
                  )}
                  title={isCollapsed ? 'Voltar ao portal' : undefined}
                >
                  <LayoutGrid className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  {!isCollapsed && <span>Voltar ao portal</span>}
                </Link>
              </li>
            </ul>
          </li>

          {/* Dashboard: só exibe se showDashboard (não escolheu allowedModules ou incluiu /dashboard) */}
          {showDashboard && (
            <li>
              <ul role="list" className="-mx-2 space-y-1">
                <li>
                  <NavLink
                    to={dashboardItem.href}
                    className={({ isActive }) =>
                      cn(
                        isActive
                          ? 'bg-muted text-foreground border-l-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted border-l-2 border-transparent',
                        'group flex gap-x-3 rounded-input px-3 py-2 font-body text-body-sm font-medium transition-all duration-200'
                      )
                    }
                    title={isCollapsed ? dashboardItem.name : undefined}
                  >
                    <dashboardItem.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {!isCollapsed && <span>{dashboardItem.name}</span>}
                  </NavLink>
                </li>
              </ul>
            </li>
          )}

          {/* Kanban Selector — apenas no módulo Cobranças (ou menu completo) */}
          {showKanbanSelector && (
            <li>
              <KanbanSelector isCollapsed={isCollapsed} />
            </li>
          )}

          {/* Outros itens de navegação */}
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => {
                const IconComponent = item.icon;
                return (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      end={item.href === PAINEL_OFICINA_BASE}
                      className={({ isActive }) =>
                        cn(
                          isActive
                            ? 'bg-muted text-foreground border-l-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted border-l-2 border-transparent',
                          'group flex gap-x-3 rounded-input px-3 py-2 font-body text-body-sm font-medium transition-all duration-200'
                        )
                      }
                      title={isCollapsed ? item.name : undefined}
                    >
                      <IconComponent className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {!isCollapsed && <span>{item.name}</span>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </li>
        </ul>
      </nav>
      
      {/* Versão do sistema na parte inferior */}
      <div className="shrink-0 border-t border-border pt-4 pb-4">
        {!isCollapsed ? (
          <div className="text-center">
            <button
              onClick={() => setIsChangelogOpen(true)}
              className="font-body text-body-xs text-muted-foreground hover:text-foreground transition-colors duration-200 cursor-pointer"
              title="Clique para ver o histórico de versões"
            >
              Versão {SYSTEM_VERSION}
            </button>
            <div className="font-body text-body-xs text-muted-foreground/70 mt-0.5">
              Atualizado em {LAST_UPDATE_DATE}
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={() => setIsChangelogOpen(true)}
              className="font-body text-body-xs text-muted-foreground hover:text-foreground transition-colors duration-200 cursor-pointer"
              title={`Versão ${SYSTEM_VERSION} - Atualizado em ${LAST_UPDATE_DATE} - Clique para ver o histórico`}
            >
              v{SYSTEM_VERSION}
            </button>
          </div>
        )}
      </div>

      {/* Modal de Changelog */}
      <ChangelogModal
        isOpen={isChangelogOpen}
        onClose={() => setIsChangelogOpen(false)}
      />
    </>
  );
};
